import Link from "next/link";
import { ArrowUpRight, type LucideIcon } from "lucide-react";
import { ImageFrame } from "@/components/ui/ImageFrame";
import { Badge } from "@/components/ui/Badge";
import { localeHeader, type Dict, type Lang } from "@/lib/i18n";
import { cn } from "@/components/ui/cn";

export type MetaFact = {
  Icon?: LucideIcon;
  label: string;
};

// Shared server/client component (rendered by server cards AND the client
// detail wrappers) — it must stay sync and dictionary-agnostic: the caller
// passes the resolved Dict (server: await getDict(lang); client: useDict()).
type CardShellProps = {
  t: Dict;
  href: string;
  openHref?: string;
  poster: string | null;
  posterFallback: string;
  typeLabel: string;
  TypeIcon?: LucideIcon;
  statusLabel?: string;
  posterBadge?: React.ReactNode;
  title: string;
  abstract?: string | null;
  facts?: MetaFact[];
  paths?: string[];
  enrolled?: boolean;
  faded?: boolean;
  ariaLabel?: string;
  /** Optional override for the CTA pill — when not supplied,
      falls back to "Continue" (enrolled) / "Explore" (not). */
  ctaLabel?: string;
};

/**
 * Stable BEM-ish class hooks on every card slot. They do no styling — the
 * actual look stays in the Tailwind utilities — but they let you say
 * 'increase card-title size' or 'card-abstract should be 1 line' and we
 * have a clear DOM handle for it.
 *
 *   .module-card                 outer <a>
 *   .module-card__poster         poster wrapper
 *   .module-card__poster-badge   only-when-urgent overlay (Live / Archive)
 *   .module-card__body           text content container
 *   .module-card__eyebrow        type + status line (above title)
 *   .module-card__type           the type label inside eyebrow
 *   .module-card__status         the status label inside eyebrow
 *   .module-card__title          h3
 *   .module-card__abstract       p with summary
 *   .module-card__keywords       wrapper for keyword pills
 *   .module-card__keyword        each pill
 *   .module-card__footer         bottom divider row
 *   .module-card__facts          list of icon+label facts
 *   .module-card__fact           each fact item
 *   .module-card__cta            the action pill
 */
export function CardShell({
  t,
  href,
  openHref,
  poster,
  posterFallback,
  typeLabel,
  TypeIcon,
  statusLabel,
  posterBadge,
  title,
  abstract,
  facts,
  paths,
  enrolled,
  faded,
  ariaLabel,
  ctaLabel,
}: CardShellProps) {
  const defaultCta = enrolled ? t.detail.continueCta : t.detail.exploreCta;
  const label = ctaLabel ?? defaultCta;

  return (
    <Link
      href={openHref ?? href}
      aria-label={ariaLabel ?? title}
      className={cn(
        "module-card group/card relative flex flex-col bg-card text-card-foreground rounded-card overflow-hidden hover:no-underline focus:outline-none border border-border/60 transition-all duration-200",
        "hover:border-foreground/15 hover:-translate-y-0.5",
        faded ? "opacity-70" : null,
      )}
    >
      <div className="module-card__poster relative overflow-hidden">
        <div className="transition-transform duration-500 ease-out group-hover/card:scale-[1.03]">
          {/* alt="" — the link's aria-label + the h3 already carry the title;
              a third copy on the poster makes screen readers announce every
              card three times. */}
          <ImageFrame
            src={poster}
            alt=""
            aspect="16/9"
            className="!border-0"
            fallback={posterFallback}
            monogram={title}
          />
        </div>
        {posterBadge ? (
          <span className="module-card__poster-badge absolute top-2.5 right-2.5">
            {posterBadge}
          </span>
        ) : null}
        {paths && paths.length > 0 ? (
          <span
            className="module-card__poster-chip absolute bottom-2.5 left-2.5 inline-flex max-w-[calc(100%-1.25rem)] items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] text-foreground bg-card/80 backdrop-blur-sm ring-1 ring-inset ring-foreground/10"
            title={paths.slice(0, 3).join(" · ")}
          >
            <span className="truncate">{paths[0]}</span>
          </span>
        ) : null}
      </div>

      <div className="module-card__body flex flex-col gap-2.5 p-6 flex-1">
        <p className="module-card__eyebrow text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
          {TypeIcon ? (
            <TypeIcon className="size-4 text-accent shrink-0" strokeWidth={2.25} aria-hidden />
          ) : null}
          <span className="module-card__type">{typeLabel}</span>
          {statusLabel ? (
            <>
              <span aria-hidden className="text-foreground/25">
                ·
              </span>
              <span className="module-card__status">{statusLabel}</span>
            </>
          ) : null}
        </p>

        <h3 className="module-card__title line-clamp-2 group-hover/card:text-primary transition-colors !text-[19px] !leading-snug !font-semibold !text-foreground !mb-0">
          {title}
        </h3>

        {abstract ? (
          <p className="module-card__abstract text-[14px] text-muted-foreground line-clamp-2 leading-snug">
            {abstract}
          </p>
        ) : null}

        {/* Wraps: at four across a card is ~245 px, and a date beside the CTA pill
            did not fit — the pill painted over the date (seen 2026-09-18 on the
            in-app news index). The pill drops under the facts and keeps to the
            right; on a wide card nothing changes. */}
        <div className="module-card__footer mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-4">
          <FactsLine facts={facts ?? []} />
          <CtaPill enrolled={enrolled ?? false} label={label} />
        </div>
      </div>
    </Link>
  );
}

// Enrolled = primary "Continue" action — always-on gold pill matching the
// dashboard CTAs (warm shadow + inner highlight). Unenrolled = browse intent,
// quieter rest state that warms to gold on row-hover so it still affords action.
function CtaPill({ enrolled, label }: { enrolled: boolean; label: string }) {
  if (enrolled) {
    return (
      <span
        className="module-card__cta ml-auto inline-flex items-center gap-1 shrink-0 text-[11px] uppercase tracking-[0.12em] font-bold rounded-full px-3 h-7 bg-accent text-accent-foreground transition-all duration-200 ease-out group-hover/card:brightness-105"
        style={{
          boxShadow:
            "0 1px 2px rgb(0 0 0 / 0.08), 0 4px 10px -4px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 30%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 60%, transparent)",
        }}
      >
        {label}
        <ArrowUpRight
          className="size-3 shrink-0 transition-transform duration-200 ease-out group-hover/card:translate-x-0.5"
          aria-hidden
        />
      </span>
    );
  }
  return (
    <span className="module-card__cta ml-auto inline-flex items-center gap-1 shrink-0 text-[11px] uppercase tracking-[0.12em] font-bold rounded-full px-3 h-7 bg-foreground/[0.06] text-foreground transition-all duration-200 ease-out group-hover/card:bg-accent group-hover/card:text-accent-foreground">
      {label}
      <ArrowUpRight
        className="size-3 shrink-0 transition-transform duration-200 ease-out group-hover/card:translate-x-0.5"
        aria-hidden
      />
    </span>
  );
}

function FactsLine({ facts }: { facts: MetaFact[] }) {
  if (facts.length === 0) return <span className="flex-1" />;
  return (
    <ul className="module-card__facts flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground min-w-0">
      {facts.map((f, i) => (
        <li
          key={i}
          className="module-card__fact inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          {f.Icon ? <f.Icon className="size-3.5 shrink-0" aria-hidden /> : null}
          <span className="truncate">{f.label}</span>
          {/* The separator trails its fact, so a wrapped line starts with the
              next fact rather than a stray "·" (seen on Avant2Go's news cards,
              2026-10-01). */}
          {i < facts.length - 1 ? (
            <span aria-hidden className="text-foreground/25">
              ·
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function FreeBadge({ t }: { t: Dict }) {
  return <Badge tone="success">{t.detail.free}</Badge>;
}

export function LiveBadge({ t }: { t: Dict }) {
  return (
    <Badge tone="teal" className="!bg-destructive !text-destructive-foreground">
      ● {t.detail.live}
    </Badge>
  );
}

export function ArchiveBadge({ t }: { t: Dict }) {
  return <Badge tone="muted">{t.listing.archive}</Badge>;
}

// Scheduled (not-yet-published) module — content is withheld until publishAt.
// The poster overlay flags it at a glance; the card's date fact carries the
// go-live date.
export function ScheduledBadge({ t }: { t: Dict }) {
  return <Badge tone="muted">{t.detail.scheduledBadge}</Badge>;
}

export function isPast(dateTo: string | null | undefined): boolean {
  if (!dateTo) return false;
  const t = new Date(dateTo).getTime();
  if (Number.isNaN(t)) return false;
  return t < Date.now();
}

/**
 * Keywords from the portal can arrive in several shapes:
 *   - plain string:        "a, b | c"
 *   - newline-separated:   "a\nb\nc"
 *   - localised JSON blob: '{"sl_SI": "a\nb\nc", "en_US": "x\ny\nz"}'
 *
 * Normalise all three into a clean list of tags.
 */
export function parsePaths(keywords: string | null | undefined, lang?: Lang): string[] {
  if (!keywords) return [];

  let raw = keywords.trim();

  // Localised JSON shape — pick the current locale, fall back to first value.
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw) as Record<string, unknown>;
      const locale = localeHeader(lang ?? "SI");
      const picked =
        (typeof obj[locale] === "string" && (obj[locale] as string)) ||
        (Object.values(obj).find((v) => typeof v === "string") as string | undefined) ||
        "";
      raw = picked.trim();
    } catch {
      // not valid JSON — fall through to delimiter split
    }
  }

  if (!raw) return [];

  return raw
    .split(/[\n,|;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
